// TURN-WINDOW ARM machine_card fixture (ME-CARDS-R1 A2 + A3 regression).
//
// Mirrors the FIRST real end-to-end MASH card that shipped to `.cc` on
// 2026-08-22 (owner screenshot `arm.PNG`) — the payload that exposed two
// renderer defects at once:
//
//  A2  The turn resolved to the window token `"yesterday"`, which is NOT one of
//      the five PINNED `windows[]` keys. The figure's PRIMARY read (σ 1.886 mm,
//      n=1073) is the real answer — the verdict headline and the provenance
//      footer are both computed on it — but the viewer projected every figure
//      onto a hardcoded enum key whose slice is `absent`, so the SIZE tiles
//      rendered "not available" OVER real data. Both `last_hour` (what the
//      deployed bundle highlighted) and `today` (what source said) are `absent`
//      here, so this fixture reproduces the bug under either vintage.
//
//  A3  BOTH SIZE figures cite the SAME source (`s_jaws_residual`). The footer
//      row template renders only source fields, so the old per-figure-per-source
//      dedupe emitted the identical `jaws_actual_mm-residual …` row TWICE.
//
// Honesty notes: the English verdict headline is bot data and renders VERBATIM
// (headline language is a bot-side contract question — B3, filed). The identity
// block carries the GUID and ONLY the GUID; `display_name` stays the honest
// machine name so this fixture is GUID-clean by construction (the A1 suite
// derives its GUID-polluted variant from it).
import type { MachineCardArtifactData } from "../../../chatBot/models/machineEvidenceModels";

export const TURN_WINDOW_ARM_MACHINE_GUID = "d72d27d9-0308-49e6-7c19-08dec02ae617";

/** The turn-resolved window token — deliberately OUTSIDE the pinned 5-key enum. */
export const TURN_WINDOW_ARM_TOKEN = "yesterday";

/** Primary (turn-resolved) reads, exported so tests assert the exact rendered
 *  numbers without duplicating literals. The `windows[]` slices for the three
 *  short windows are `absent` — a click on them MUST still say "not available". */
export const TURN_WINDOW_ARM_VALUES = {
  residualStdDev: 1.886,
  residualN: 1073,
  outsideBand: 41,
  thisWeek: 2.014,
  allTime: 1.732,
} as const;

export const TURN_WINDOW_ARM_CARD: MachineCardArtifactData = {
  schema_version: 1,
  mode: "snapshot",
  machine: {
    machine: TURN_WINDOW_ARM_MACHINE_GUID,
    machine_id_full: TURN_WINDOW_ARM_MACHINE_GUID,
    class_template: null,
    display_name: "MASHi2601",
  },
  freshness: {
    // Mash_1 has been idle since 2026-08-21 afternoon — STALE is the honest,
    // bot-authoritative state (never recomputed renderer-side).
    state: "STALE",
    as_of: "2026-08-22T07:15:00+00:00",
    last_sample_at: "2026-08-21T14:18:00+02:00",
    age_seconds: 61020,
    source: "mash_liveness",
    provenance: "live",
    event_time: null,
  },
  tabs: [
    {
      part_slug: "arm",
      name: "Arm",
      parent_slug: null,
      verdict: {
        status: "stale",
        headline: "Arm: Jaw residual (actual – setpoint) stddev 1.886 mm this window",
        detail: null,
        basis: "figure values (no calibrated threshold this milestone)",
      },
      lane_coverage: {},
      figures: [
        {
          // SIZE #1 — the residual band figure; the ONLY figure carrying a
          // `threshold` (the A5 gauge-band centerpiece renders from this).
          id: "f_jaws_residual_stddev",
          label: "Jaw residual (actual – setpoint) stddev",
          value: TURN_WINDOW_ARM_VALUES.residualStdDev,
          unit: "mm",
          window: TURN_WINDOW_ARM_TOKEN,
          window_start: "2026-08-21T00:00:00+02:00",
          window_end: "2026-08-22T00:00:00+02:00",
          denominator: null,
          coverage: "ok",
          measure_basis: null,
          threshold: { target: 0, low: -1.5, high: 1.5 },
          n: TURN_WINDOW_ARM_VALUES.residualN,
          source_ref: "s_jaws_residual",
          freshness: null,
          breakdown: null,
          windows: [
            // Machine idle since yesterday afternoon => the three short windows
            // carry no data at all. `absent`, NEVER a scaled or zeroed value.
            { window_key: "last_hour", value: null, count: null, coverage: "absent", source_ref: "s_jaws_residual" },
            { window_key: "this_shift", value: null, count: null, coverage: "absent", source_ref: "s_jaws_residual" },
            { window_key: "today", value: null, count: null, coverage: "absent", source_ref: "s_jaws_residual" },
            { window_key: "this_week", value: TURN_WINDOW_ARM_VALUES.thisWeek, count: 6841, coverage: "ok", source_ref: "s_jaws_residual" },
            { window_key: "all_time", value: TURN_WINDOW_ARM_VALUES.allTime, count: 214508, coverage: "ok", source_ref: "s_jaws_residual" },
          ],
        },
        {
          // SIZE #2 — cites the SAME source as #1 (the A3 duplicate-row case).
          id: "f_jaws_outside_band",
          label: "Parts outside own tolerance band",
          value: TURN_WINDOW_ARM_VALUES.outsideBand,
          unit: null,
          window: TURN_WINDOW_ARM_TOKEN,
          window_start: "2026-08-21T00:00:00+02:00",
          window_end: "2026-08-22T00:00:00+02:00",
          denominator: { num: TURN_WINDOW_ARM_VALUES.outsideBand, den: TURN_WINDOW_ARM_VALUES.residualN },
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: TURN_WINDOW_ARM_VALUES.residualN,
          source_ref: "s_jaws_residual",
          freshness: null,
          breakdown: null,
          windows: [
            { window_key: "last_hour", value: null, count: null, coverage: "absent", source_ref: "s_jaws_residual" },
            { window_key: "this_shift", value: null, count: null, coverage: "absent", source_ref: "s_jaws_residual" },
            { window_key: "today", value: null, count: null, coverage: "absent", source_ref: "s_jaws_residual" },
            { window_key: "this_week", value: 263, count: 6841, coverage: "ok", source_ref: "s_jaws_residual" },
            { window_key: "all_time", value: 7412, count: 214508, coverage: "ok", source_ref: "s_jaws_residual" },
          ],
        },
      ],
      trend: null,
      live: null,
    },
  ],
  sources: [
    {
      id: "s_jaws_residual",
      signal_key: "jaws_actual_mm-residual",
      window: TURN_WINDOW_ARM_TOKEN,
      window_start: "2026-08-21T00:00:00+02:00",
      window_end: "2026-08-22T00:00:00+02:00",
      count: TURN_WINDOW_ARM_VALUES.residualN,
      source_grain: "numeric",
      read_source: "telemetry store",
      edge_path: null,
    },
  ],
  frozen: { is_frozen: false, frozen_at: null },
};
